import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@jk-core/utils';
import DownIcon from './arrow-down.svg';
import styles from './DropDown.module.scss';
import List from './List';

interface DropDownProps {
  /**
   * 드롭다운 목록
   */
  list: string[];

  /**
   * 드롭다운 버튼에 표시될 내용
   */
  value?: string;
  /**
   * 선택된 아이템을 처리하는 함수
   */
  onSelect: (item: string, index:number) => void;
  /**
   * 필터링 기능 활성화 여부
   */
  filter?: boolean;
  /**
   * 드롭다운의 플레이스홀더 텍스트
   */
  placeholder?: string;
  /**
   * 드롭다운의 스타일
   */
  style?: React.CSSProperties;
}

/**
 * 드롭다운 컴포넌트
 * 사용자가 선택할 수 있는 목록을 제공하며, 선택된 아이템을 표시합니다.
 * 필터링 기능을 활성화할 수 있으며, 플레이스홀더 텍스트를 설정할 수 있습니다.
 */
export default function DropDown({
  list = [], value, onSelect, filter,
  placeholder, style }: DropDownProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = useState<string>(placeholder || '선택하세요');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredList, setFilteredList] = useState(list);

  useEffect(() => {
    if (!isOpen) {
      setFilteredList(list);
    }

    function handleClick(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        listRef.current &&
        !listRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, list]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={styles.title}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={style}
      >
        <span>{value || selectedItem}</span>
        <DownIcon className={cn({
          [styles.icon]: true,
          [styles['icon--up']]: isOpen,
        })}
        />
      </button>
      {isOpen && (
        createPortal(
          <div ref={listRef} className={styles['list-portal']}>
            <List
              selectedItem={selectedItem}
              list={list}
              filteredList={filteredList}
              parent={wrapperRef}
              setFilteredList={filter ? setFilteredList : undefined}
              onSelect={(item, index) => {
                onSelect(item, index);
                setSelectedItem(item);
                setFilteredList(list);
                setIsOpen(false);
              }}
            />
          </div>, document.body)
      )}
    </div>
  );
}